import React, { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { DataView } from 'primereact/dataview';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { useNavigate } from 'react-router-dom';
import { WorkflowExecution } from '../types';

interface GlobalWorkflowManagerProps {
  visible: boolean;
  onHide: () => void;
}

interface ProjectWorkflow {
  projectId: string;
  projectTitle: string;
  workflow: WorkflowExecution;
}

const GlobalWorkflowManager: React.FC<GlobalWorkflowManagerProps> = ({
  visible,
  onHide
}) => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<ProjectWorkflow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      loadAllWorkflows();
    }
  }, [visible]);

  const loadAllWorkflows = async () => {
    try {
      setLoading(true);
      // This would need a new API endpoint to get all active workflows across projects
      // For now, we'll use a mock implementation
      
      // In a real implementation, you would call:
      // const response = await apiService.getAllActiveWorkflows();
      
      // Mock data for demonstration
      setWorkflows([]);
    } catch (error) {
      console.error('Failed to load workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
      case 'TIMED_OUT':
      case 'ABORTED':
        return 'danger';
      case 'RUNNING':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getProgressValue = (workflow: WorkflowExecution): number => {
    if (workflow.status === 'SUCCEEDED') return 100;
    if (workflow.status === 'FAILED') return 0;
    
    // Calculate based on current step
    const stepProgress: { [key: string]: number } = {
      'ExtractIndicators': 33,
      'DesignQuestionnaire': 66,
      'DeployToKobo': 90
    };
    
    return stepProgress[workflow.current_step || ''] || 10;
  };

  const formatElapsedTime = (startedAt: string): string => {
    if (!startedAt) return 'N/A';
    
    const startTime = new Date(startedAt).getTime();
    if (isNaN(startTime)) return 'N/A';
    
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    
    // Handle negative or invalid durations
    if (elapsed < 0) return 'just now';
    if (elapsed < 1) return 'just now';
    if (elapsed < 60) return `${elapsed}s ago`;
    if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ago`;
    if (elapsed < 86400) return `${Math.floor(elapsed / 3600)}h ago`;
    
    const days = Math.floor(elapsed / 86400);
    return `${days}d ago`;
  };

  const itemTemplate = (item: ProjectWorkflow) => {
    return (
      <div
        style={{
          padding: '1rem',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          marginBottom: '0.75rem',
          cursor: 'pointer',
          transition: 'all 0.2s',
          backgroundColor: 'white'
        }}
        onClick={() => {
          navigate(`/app/projects/${item.projectId}/workflow`);
          onHide();
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f8f9fa';
          e.currentTarget.style.borderColor = '#3b82f6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'white';
          e.currentTarget.style.borderColor = '#e5e7eb';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
              {item.projectTitle}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {item.workflow.current_step && `Step: ${item.workflow.current_step}`}
            </div>
          </div>
          <Tag
            value={item.workflow.status}
            severity={getStatusSeverity(item.workflow.status)}
            style={{ fontSize: '0.75rem' }}
          />
        </div>

        <ProgressBar
          value={getProgressValue(item.workflow)}
          style={{ height: '6px', marginBottom: '0.5rem' }}
          showValue={false}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            Started {formatElapsedTime(item.workflow.started_at)}
          </span>
          <Button
            icon="pi pi-arrow-right"
            text
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/app/projects/${item.projectId}/workflow`);
              onHide();
            }}
          />
        </div>
      </div>
    );
  };

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937' }}>
        Active Workflows
      </h3>
      <Button
        icon="pi pi-refresh"
        text
        size="small"
        onClick={loadAllWorkflows}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      style={{ width: '400px' }}
      header={header}
    >
      {workflows.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: '#6b7280'
        }}>
          <i className="pi pi-inbox" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}></i>
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            No active workflows at the moment
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem' }}>
            Start a workflow from any project to see it here
          </p>
        </div>
      ) : (
        <DataView
          value={workflows}
          itemTemplate={itemTemplate}
          layout="list"
        />
      )}
    </Sidebar>
  );
};

export default GlobalWorkflowManager;
